import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    TextField,
    CircularProgress,
    Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import vneidSdkService from '../../../services/vneidSdkService';

export default function TestSdkModal({ open, onClose }) {
    const { t } = useTranslation();
    const [key, setKey] = useState('');
    const [version, setVersion] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const handleClose = () => {
        setKey('');
        setVersion('');
        setResult(null);
        onClose();
    };

    const handleTest = async () => {
        if (!key.trim() || !version.trim()) {
            setResult({ success: false, message: t('vneid.sdk.testSdk.requiredFields') });
            return;
        }

        setLoading(true);
        setResult(null);

        try {
            const response = await vneidSdkService.testConnection(key.trim(), version.trim());
            setResult(response);
        } catch (err) {
            // Handle error response (400, 500, etc.)
            const errorResponse = err.response?.data || err;
            setResult({
                success: errorResponse.success ?? false,
                message: errorResponse.message || err.message || t('vneid.sdk.testSdk.error'),
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {t('vneid.sdk.testSdk.title')}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label={t('vneid.sdk.testSdk.key')}
                        value={key}
                        onChange={(e) => setKey(e.target.value)}
                        fullWidth
                        required
                        id="testSdkKey"
                        name="testSdkKey"
                        data-testid="test-sdk-modal-input-key"
                    />
                    <TextField
                        label={t('vneid.sdk.testSdk.version')}
                        value={version}
                        onChange={(e) => setVersion(e.target.value)}
                        fullWidth
                        required
                        id="testSdkVersion"
                        name="testSdkVersion"
                        data-testid="test-sdk-modal-input-version"
                    />

                    {result && (
                        <Alert severity={result.success ? 'success' : 'error'}>
                            {result.message}
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                    id="cancelTestSdk"
                    name="cancelTestSdk"
                    data-testid="test-sdk-modal-button-cancel"
                >
                    {t('common.close')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleTest}
                    disabled={loading}
                    id="confirmTestSdk"
                    name="confirmTestSdk"
                    data-testid="test-sdk-modal-button-test"
                >
                    {loading ? <CircularProgress size={20} /> : t('vneid.sdk.testSdk.test')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
